import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, expenseCategories, getCategoryLabel } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileSpreadsheet,
  Search,
  Download,
  Filter,
  TrendingDown,
  Building2,
  CreditCard,
  ShieldCheck,
  ShieldX,
  Calendar,
  IndianRupee
} from 'lucide-react';

export default function FinalExpenseSheet() {
  const [expenses, setExpenses] = useState([]);
  const [ccExpenses, setCcExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [ventures, setVentures] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    source: 'all', // all, bank, cc
    verified: 'all', // all, verified, unverified
    category: 'all',
    start_date: '',
    end_date: '',
    venture_id: 'all'
  });

  const allCategories = [...expenseCategories, ...customCategories.map(c => ({ value: c.value, label: c.label }))];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expensesRes, venturesRes, catsRes, cardsRes] = await Promise.all([
        api.get('/expenses'),
        api.get('/ventures'),
        api.get('/categories/custom').catch(() => ({ data: [] })),
        api.get('/credit-cards').catch(() => ({ data: [] }))
      ]);
      
      setExpenses(expensesRes.data);
      setVentures(venturesRes.data);
      setCustomCategories(catsRes.data);

      // Fetch CC transactions from all cards
      const allCcTxs = [];
      for (const card of cardsRes.data) {
        try {
          const txRes = await api.get(`/credit-cards/${card.card_id}/transactions`);
          txRes.data.forEach(tx => {
            if (tx.type === 'debit') {
              allCcTxs.push({ ...tx, card_name: card.name, source: 'credit_card' });
            }
          });
        } catch {}
      }
      setCcExpenses(allCcTxs);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || '';

  // Combine all expenses
  const allExpenses = [
    ...expenses.map(e => ({
      id: e.expense_id,
      date: e.date,
      source: e.source === 'bank_statement' ? 'bank' : 'bank',
      description: e.vendor || e.notes || 'Bank Expense',
      category: e.category,
      amount: e.amount,
      venture_id: e.venture_id,
      verified: e.verification_status === 'verified' || e.source === 'bank_statement' || !!e.notes?.includes('[Bank Import]'),
      transaction_type: 'business'
    })),
    ...ccExpenses.map(cc => ({
      id: cc.transaction_id,
      date: cc.date,
      source: 'credit_card',
      description: cc.description,
      category: cc.category,
      amount: cc.business_amount || cc.total_amount || cc.amount || 0,
      venture_id: cc.venture_id,
      verified: cc.verification_status === 'verified',
      transaction_type: cc.transaction_type || 'business',
      card_name: cc.card_name
    }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Apply filters
  const filteredExpenses = allExpenses.filter(exp => {
    // Source filter
    if (filters.source !== 'all') {
      if (filters.source === 'bank' && exp.source !== 'bank') return false;
      if (filters.source === 'cc' && exp.source !== 'credit_card') return false;
    }
    // Verified filter
    if (filters.verified !== 'all') {
      if (filters.verified === 'verified' && !exp.verified) return false;
      if (filters.verified === 'unverified' && exp.verified) return false;
    }
    // Category filter
    if (filters.category !== 'all' && exp.category !== filters.category) return false;
    // Venture filter
    if (filters.venture_id !== 'all' && exp.venture_id !== filters.venture_id) return false;
    // Date filters
    if (filters.start_date && exp.date < filters.start_date) return false;
    if (filters.end_date && exp.date > filters.end_date) return false;
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return exp.description?.toLowerCase().includes(q) || getCategoryLabel(exp.category)?.toLowerCase().includes(q);
    }
    return true;
  });

  // Calculate totals
  const totalAmount = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const bankTotal = filteredExpenses.filter(e => e.source === 'bank').reduce((sum, e) => sum + e.amount, 0);
  const ccTotal = filteredExpenses.filter(e => e.source === 'credit_card').reduce((sum, e) => sum + e.amount, 0);
  const verifiedTotal = filteredExpenses.filter(e => e.verified).reduce((sum, e) => sum + e.amount, 0);
  const unverifiedTotal = filteredExpenses.filter(e => !e.verified).reduce((sum, e) => sum + e.amount, 0);
  const unverifiedExpenses = filteredExpenses.filter(e => !e.verified);

  const toggleSelectExpense = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectAllUnverified = () => {
    const ids = unverifiedExpenses.map(e => e.id);
    setSelectedIds(ids);
  };

  const handleBulkVerify = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select expenses to verify');
      return;
    }
    try {
      // Separate bank expenses and CC expenses
      const bankExpenseIds = selectedIds.filter(id => {
        const exp = filteredExpenses.find(e => e.id === id);
        return exp && exp.source === 'bank';
      });
      const ccTxIds = selectedIds.filter(id => {
        const exp = filteredExpenses.find(e => e.id === id);
        return exp && exp.source === 'credit_card';
      });

      if (bankExpenseIds.length > 0) {
        await api.post('/expenses/bulk-verify', { expense_ids: bankExpenseIds });
      }
      if (ccTxIds.length > 0) {
        await api.post('/credit-cards/transactions/bulk-verify', { transaction_ids: ccTxIds });
      }
      
      toast.success(`Verified ${selectedIds.length} expenses`);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to verify expenses');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Source', 'Description', 'Category', 'Amount', 'Verified', 'Venture'];
    const rows = filteredExpenses.map(e => [
      e.date,
      e.source === 'bank' ? 'Bank' : 'Credit Card',
      e.description,
      getCategoryLabel(e.category),
      e.amount,
      e.verified ? 'Yes' : 'No',
      getVentureName(e.venture_id) || '-'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense_sheet_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="final-expense-sheet-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-red-500" />
            Final Expense Sheet
          </h1>
          <p className="text-zinc-500 mt-1">Consolidated view of all expenses from Bank and Credit Cards</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button onClick={handleBulkVerify} className="bg-green-600 hover:bg-green-700 rounded-xl" data-testid="bulk-verify-btn">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verify Selected ({selectedIds.length})
            </Button>
          )}
          {unverifiedExpenses.length > 0 && selectedIds.length === 0 && (
            <Button onClick={selectAllUnverified} variant="outline" className="text-amber-600 border-amber-200 rounded-xl" data-testid="select-unverified-btn">
              <ShieldX className="w-4 h-4 mr-2" />
              Select Unverified ({unverifiedExpenses.length})
            </Button>
          )}
          <Button onClick={handleExportCSV} variant="outline" className="rounded-xl" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Total Expenses</p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Bank Expenses</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(bankTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">CC Expenses</p>
                <p className="text-lg font-bold text-purple-600">{formatCurrency(ccTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Verified</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(verifiedTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <ShieldX className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Unverified</p>
                <p className="text-lg font-bold text-amber-600">{formatCurrency(unverifiedTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="roch-card mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input 
                placeholder="Search expenses..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-9 h-9" 
                data-testid="expense-search" 
              />
            </div>
            <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
              <SelectTrigger className="w-[130px] h-9" data-testid="filter-source">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="cc">Credit Card</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.verified} onValueChange={(v) => setFilters({ ...filters, verified: v })}>
              <SelectTrigger className="w-[130px] h-9" data-testid="filter-verified">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-[150px] h-9" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {allCategories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.venture_id} onValueChange={(v) => setFilters({ ...filters, venture_id: v })}>
              <SelectTrigger className="w-[150px] h-9" data-testid="filter-venture">
                <SelectValue placeholder="Venture" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ventures</SelectItem>
                {ventures.map(v => (
                  <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input 
                type="date" 
                value={filters.start_date} 
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} 
                className="w-[130px] h-9" 
                data-testid="filter-start-date" 
              />
              <span className="text-zinc-400">to</span>
              <Input 
                type="date" 
                value={filters.end_date} 
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} 
                className="w-[130px] h-9" 
                data-testid="filter-end-date" 
              />
            </div>
            {(filters.source !== 'all' || filters.verified !== 'all' || filters.category !== 'all' || filters.venture_id !== 'all' || filters.start_date || filters.end_date) && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilters({ source: 'all', verified: 'all', category: 'all', venture_id: 'all', start_date: '', end_date: '' })} 
                data-testid="clear-filters-btn"
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expense Table */}
      <Card className="roch-card">
        <CardContent className="p-0">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
              <h3 className="font-medium text-zinc-900 mb-1">No expenses found</h3>
              <p className="text-sm text-zinc-500">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b">
                  <tr>
                    <th className="w-10 p-3"></th>
                    <th className="text-left p-3 text-xs font-semibold text-zinc-600 uppercase">Date</th>
                    <th className="text-left p-3 text-xs font-semibold text-zinc-600 uppercase">Source</th>
                    <th className="text-left p-3 text-xs font-semibold text-zinc-600 uppercase">Description</th>
                    <th className="text-left p-3 text-xs font-semibold text-zinc-600 uppercase">Category</th>
                    <th className="text-center p-3 text-xs font-semibold text-zinc-600 uppercase">Verified</th>
                    <th className="text-left p-3 text-xs font-semibold text-zinc-600 uppercase">Venture</th>
                    <th className="text-right p-3 text-xs font-semibold text-zinc-600 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((exp) => (
                    <tr key={exp.id} className={`border-b hover:bg-zinc-50 ${exp.verified ? 'bg-green-50/30' : ''}`} data-testid={`expense-row-${exp.id}`}>
                      <td className="p-3">
                        {!exp.verified && (
                          <input 
                            type="checkbox" 
                            checked={selectedIds.includes(exp.id)}
                            onChange={() => toggleSelectExpense(exp.id)}
                            className="w-4 h-4 rounded border-zinc-300 text-green-600 focus:ring-green-500"
                          />
                        )}
                      </td>
                      <td className="p-3 text-sm text-zinc-500">{formatDate(exp.date)}</td>
                      <td className="p-3">
                        {exp.source === 'bank' ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                            <Building2 className="w-3 h-3" />
                            Bank
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-600 border border-purple-200">
                            <CreditCard className="w-3 h-3" />
                            {exp.card_name || 'CC'}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="font-medium text-zinc-800">{exp.description}</span>
                        {exp.transaction_type && exp.source === 'credit_card' && (
                          <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
                            exp.transaction_type === 'business' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                            exp.transaction_type === 'personal' ? 'bg-purple-50 text-purple-600 border-purple-200' :
                            'bg-amber-50 text-amber-600 border-amber-200'
                          } border`}>
                            {exp.transaction_type}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-zinc-600">{getCategoryLabel(exp.category) || '-'}</td>
                      <td className="p-3 text-center">
                        {exp.verified ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-600">
                            <ShieldCheck className="w-3 h-3" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-600">
                            <ShieldX className="w-3 h-3" />
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-zinc-600">{getVentureName(exp.venture_id) || '-'}</td>
                      <td className="p-3 text-right font-semibold text-red-600">{formatCurrency(exp.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-zinc-100 border-t-2">
                  <tr>
                    <td colSpan={6} className="p-3 text-sm font-semibold text-zinc-700">
                      Total ({filteredExpenses.length} expenses)
                    </td>
                    <td className="p-3 text-right font-bold text-red-600 text-lg">
                      {formatCurrency(totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
